import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wrench, CheckCircle2, AlertCircle } from "lucide-react";

export default function ClientRegisterPage() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        nome: "",
        email: "",
        password: "",
        telefone: "",
        morada: "",
        cod_postal: "",
    });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        if (form.password.length < 6) {
            setError("A palavra-passe deve ter pelo menos 6 caracteres.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/public/register`, form);
            setSuccess(true);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao submeter registo.");
        } finally {
            setSubmitting(false);
        }
    }

    if (success) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6 py-10">
                <div className="w-full max-w-md border border-green-200 bg-white p-8 text-center" data-testid="register-success">
                    <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-green-700" />
                    <h2 className="font-['Outfit'] text-2xl font-semibold text-zinc-900">
                        Registo submetido!
                    </h2>
                    <p className="mt-3 text-sm text-zinc-600">
                        A sua conta ficou pendente de aprovação pelo administrador da oficina.
                        Assim que for aprovada, poderá fazer login normalmente.
                    </p>
                    <Link
                        to="/entrar"
                        className="mt-6 inline-block rounded-sm bg-[#FF3B30] px-4 py-2 text-sm font-semibold text-white hover:bg-[#D63026]"
                    >
                        Voltar ao login
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6 py-10">
            <div className="w-full max-w-lg">
                <Link to="/" className="mb-6 inline-flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#FF3B30] text-white">
                        <Wrench className="h-5 w-5" />
                    </div>
                    <span className="font-['Outfit'] text-lg font-semibold">Oficina Moto</span>
                </Link>

                <h1 className="mb-2 font-['Outfit'] text-3xl font-semibold tracking-tight text-zinc-900">
                    Criar conta de cliente
                </h1>
                <p className="mb-8 text-sm text-zinc-600">
                    Preencha os seus dados. A conta será activada após aprovação da oficina.
                </p>

                <form onSubmit={onSubmit} className="space-y-4 border border-zinc-200 bg-white p-6" data-testid="register-form">
                    <F label="Nome completo *">
                        <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required className="h-10 rounded-sm" data-testid="reg-nome" />
                    </F>
                    <div className="grid grid-cols-2 gap-3">
                        <F label="Email *">
                            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="h-10 rounded-sm" data-testid="reg-email" />
                        </F>
                        <F label="Palavra-passe *">
                            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="h-10 rounded-sm" data-testid="reg-password" />
                        </F>
                    </div>
                    <F label="Telefone">
                        <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="h-10 rounded-sm" data-testid="reg-telefone" />
                    </F>
                    <F label="Morada">
                        <Input value={form.morada} onChange={(e) => setForm({ ...form, morada: e.target.value })} className="h-10 rounded-sm" data-testid="reg-morada" />
                    </F>
                    <F label="Código Postal">
                        <Input value={form.cod_postal} onChange={(e) => setForm({ ...form, cod_postal: e.target.value })} placeholder="0000-000" className="h-10 rounded-sm" data-testid="reg-cod-postal" />
                    </F>

                    {error ? (
                        <div className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="register-error">
                            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    ) : null}

                    <Button
                        type="submit"
                        disabled={submitting}
                        className="h-11 w-full rounded-sm bg-[#FF3B30] hover:bg-[#D63026]"
                        data-testid="register-submit"
                    >
                        {submitting ? "A submeter..." : "Submeter registo"}
                    </Button>
                </form>

                <div className="mt-6 text-center text-sm text-zinc-600">
                    Já tem conta?{" "}
                    <Link to="/entrar" className="font-semibold text-[#FF3B30] hover:underline">
                        Entrar
                    </Link>
                </div>
                <div className="mt-3 text-center text-xs text-zinc-500">
                    <Link to="/" className="hover:text-zinc-900">← Voltar à página inicial</Link>
                </div>
            </div>
        </div>
    );
}

function F({ label, children }) {
    return (
        <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
